<template src="./list-keywords-v2.component.html"></template>
<style scoped src="./list-keywords-v2.component.css"></style>
<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import Modal from '@/components/modal/modal.component.vue';
import Sidebar from '@/components/sidebar/sidebar.component.vue';
import api from '@/api';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import {toasted} from '@/plugins/toasted';
import {Filter, getErrorMessage, Pagination, Sorting, getIsoDate, noop, isNil, languageCountryCode} from '@/helpers';
import {ImagesArchiveV2, KeywordV2, Vertical} from '@/models';

class ListKeywordsV2Filter<T extends {[key: string]: string}> extends Filter<T> {
  from: string = '';
  to: string = '';

  get archiveDate() {
    return [this.from, this.to];
  }

  set archiveDate([from, to]) {
    this.from = from;
    this.to = to;
  }
}

export default Vue.extend({
  components: {
    Modal,
    Sidebar,
    DatePicker,
  },
  data() {
    return {
      ac: null as AbortController | null,
      keywords: [] as KeywordV2[],
      editKeywordModal: {
        keyword: null as KeywordV2 | null,
      },
      removeKeywordModal: {
        keyword: null as KeywordV2 | null,
      },
      removeImageModal: {
        image: null as KeywordV2['images'][number] | null,
        deleteFromAllKeywords: false,
      },
      showLoading: false,
      isDownloadingFile: false,
      isDraggingImages: false,
      isUploadingImages: false,
      imageUploadProgress: {
        completed: 0,
        total: 0,
      },
      imageUploadErrors: [] as string[],
      options: {
        languages: ['null', ...Object.keys(languageCountryCode).sort()],
        verticals: [] as string[],
        scrapedVerticals: [] as string[],
        scraped: ['true', 'false'],
      },
      filter: new ListKeywordsV2Filter({
        id: '',
        keyword: '',
        market: '',
        language: '',
        vertical: '',
        from: '',
        to: '',
        scraped: '',
        numberOfImages: '',
      }),
      sorting: new Sorting({
        column: 'id',
        direction: 'desc',
      }),
      pagination: new Pagination({
        perPage: 50,
      }),
      form: {
        keyword: '',
        vertical: null as string | null,
        scraped_vertical: null as string | null,
      },
      // copying images
      fromKeyword: null as KeywordV2 | null,
      toKeywords: [] as KeywordV2[],
      hoverPreview: {
        left: 0,
        maxHeight: 0,
        placement: 'above' as 'above' | 'below',
        top: 0,
        url: '',
        policyCompliance: '',
        keywordAdherence: '',
        complianceClass: '',
      },
    };
  },
  watch: {
    '$route.query': {
      async handler(query) {
        this.filter.loadFromQuery(query);
        this.sorting.loadFromQuery(query);
        this.pagination.loadFromQuery(query);
        await this.getKeywords();
      },
      immediate: true,
    },
  },
  async created() {
    await this.getVerticals();
  },
  mounted() {
    document.body.classList.add(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  destroyed() {
    this.hideImagePreview();
    document.body.classList.remove(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  filters: {
    formatDate(value: string | Date) {
      if (!value) return '';
      return getIsoDate(new Date(value));
    },
    formatKeyword(value: string) {
      if (!value) return '';
      return String(value)
        .split(/(\s+)/)
        .map(token => token.trim()
          ? token.charAt(0).toUpperCase() + token.slice(1)
          : token)
        .join('');
    },
  },
  methods: {
    async getKeywords() {
      try {
        this.showLoading = true;
        const searchParams = this.getSearchParams();
        this.ac?.abort();
        this.ac = new AbortController();
        const {rows, count} = await api.execute(
          'get',
          `/campaigns-archive/keywords-v2?${searchParams.toString()}`,
          null,
          null,
          this.ac.signal,
        ) as {rows: KeywordV2[], count: number};
        this.keywords = rows;
        this.pagination.total = count;
      } catch (e) {
        console.error(e);
        if (!axios.isCancel(e)) {
          toasted.showErrorMessage(getErrorMessage(e));
        }
      } finally {
        this.showLoading = false;
      }
    },
    async getVerticals() {
      try {
        const verticals = await api.execute('get', '/verticals') as Vertical[];
        this.options.verticals = ['null', ...verticals.map(el => el.name)];
      } catch (e) {
        console.error(e);
        toasted.showErrorMessage(getErrorMessage(e));
      }
    },
    onFilter() {
      this.resetFromKeywordToKeyword();
      this.updateQueryParams({...this.filter, page: 1});
    },
    onReset() {
      this.filter.reset();
      this.resetFromKeywordToKeyword();
      this.updateQueryParams({page: 1});
    },
    getMarketFlag(market: string) {
      const normalized = String(market ?? '').trim().toUpperCase();
      const flagCode = normalized === 'UK' ? 'GB' : normalized;
      if (!/^[A-Z]{2}$/.test(flagCode)) return '';
      const codePoints = Array.from(flagCode).map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    },
    getLanguageFlag(language: string) {
      const countryCode = languageCountryCode[language];
      return this.getMarketFlag(countryCode);
    },
    updatePage(page: number) {
      this.resetFromKeywordToKeyword();
      this.updateQueryParams({...this.filter, page});
    },
    getSearchParams() {
      const searchParams = new URLSearchParams();
      this.filter.buildSearchParams(searchParams);
      this.sorting.buildSearchParams(searchParams);
      this.pagination.buildSearchParams(searchParams);
      return searchParams;
    },
    async openEditKeywordModal(keyword: KeywordV2) {
      this.resetFromKeywordToKeyword();
      this.editKeywordModal.keyword = keyword;
      this.form.keyword = keyword.keyword;
      this.form.vertical = keyword.vertical;
      this.form.scraped_vertical = keyword.scraped_vertical;
      await this.getScrapedVerticals(keyword.market);
    },
    async getScrapedVerticals(market: string) {
      try {
        const searchParams = new URLSearchParams();
        searchParams.append('market', market);
        const verticals = await api.execute('get', `/campaigns-archive/scraped-verticals?${searchParams.toString()}`) as string[];
        this.options.scrapedVerticals = ['null', ...verticals];
      } catch (e) {
        console.error(e);
        toasted.showErrorMessage(getErrorMessage(e));
      }
    },
    getImageComplianceClass(image: KeywordV2['images'][number]) {
      const hasCompliance = !isNil(image.policy_compliance);
      const hasAdherence = !isNil(image.keyword_adherence);
      if (!hasCompliance || !hasAdherence) {
        return 'keywords-list__image-item--unknown';
      }

      const policyCompliant = image.policy_compliance === 'in policy';
      const keywordAdherent = image.keyword_adherence === 1;
      return policyCompliant && keywordAdherent
        ? 'keywords-list__image-item--compliant'
        : 'keywords-list__image-item--non-compliant';
    },
    closeEditKeywordModal() {
      this.editKeywordModal.keyword = null;
      this.form.keyword = '';
      this.form.vertical = null;
      this.form.scraped_vertical = null;
      this.options.scrapedVerticals = [];
      this.isDraggingImages = false;
      this.imageUploadErrors = [];
    },
    async updateKeyword() {
      if (this.editKeywordModal.keyword) {
        try {
          await api.execute(
            'patch',
            `/campaigns-archive/keywords-v2/${this.editKeywordModal.keyword.id}`,
            this.form,
          );
          this.keywords = this.keywords.map(el => el.id === this.editKeywordModal.keyword?.id ? {...el, ...this.form} : el);
          this.closeEditKeywordModal();
          toasted.showSuccessMessage('Keyword updated');
        } catch (e) {
          console.error(e);
          toasted.showErrorMessage(getErrorMessage(e));
        }
      }
    },
    openRemoveKeywordModal(keyword: KeywordV2) {
      this.removeKeywordModal.keyword = keyword;
    },
    closeRemoveKeywordModal() {
      this.removeKeywordModal.keyword = null;
    },
    async removeKeyword() {
      if (this.removeKeywordModal.keyword) {
        try {
          await api.execute('post', '/campaigns-archive/delete', {ids: [this.removeKeywordModal.keyword.id]});
          this.keywords = this.keywords.filter(el => el.id !== this.removeKeywordModal.keyword?.id);
          this.closeRemoveKeywordModal();
          toasted.showSuccessMessage('Keyword deleted');
        } catch (e) {
          console.error(e);
          toasted.showErrorMessage(getErrorMessage(e));
        }
      }
    },
    openImagePicker() {
      if (!this.isUploadingImages) {
        (this.$refs.imageFileInput as HTMLInputElement).click();
      }
    },
    onImageDragOver() {
      if (!this.isUploadingImages) this.isDraggingImages = true;
    },
    onImageDragLeave() {
      this.isDraggingImages = false;
    },
    async onImageDrop(event: DragEvent) {
      this.isDraggingImages = false;
      if (event.dataTransfer) await this.uploadImages(Array.from(event.dataTransfer.files));
    },
    async uploadImage(event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.files) await this.uploadImages(Array.from(target.files));
      target.value = '';
    },
    isSupportedImage(file: File) {
      return file.type === 'image/jpeg' || file.type === 'image/png';
    },
    async uploadImages(files: File[]) {
      const keyword = this.editKeywordModal.keyword;
      if (!keyword || this.isUploadingImages || !files.length) return;

      const invalidFiles = files.filter(file => !this.isSupportedImage(file));
      const validFiles = files.filter(file => this.isSupportedImage(file));
      this.imageUploadErrors = invalidFiles.map(file => `${file.name}: only JPEG and PNG files are supported.`);

      if (invalidFiles.length) {
        toasted.showErrorMessage(`${invalidFiles.length} unsupported file${invalidFiles.length === 1 ? '' : 's'} skipped. Only JPEG and PNG images can be uploaded.`);
      }
      if (!validFiles.length) return;

      this.isUploadingImages = true;
      this.imageUploadProgress = {completed: 0, total: validFiles.length};
      let successfulUploads = 0;
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append('keyword', keyword.keyword);
        formData.append('market', keyword.market);
        formData.append('file', file);
        try {
          const image = await api.execute('post', '/images-archive-v2', formData) as ImagesArchiveV2;
          keyword.images = keyword.images.concat(image);
          successfulUploads += 1;
        } catch (e) {
          console.error(e);
          this.imageUploadErrors.push(`${file.name}: ${getErrorMessage(e)}`);
        } finally {
          this.imageUploadProgress.completed += 1;
        }
      }
      this.isUploadingImages = false;
      if (successfulUploads) {
        toasted.showSuccessMessage(`${successfulUploads} image${successfulUploads === 1 ? '' : 's'} uploaded.`);
      }
      if (this.imageUploadErrors.length > invalidFiles.length) {
        toasted.showErrorMessage('Some images could not be uploaded. See the upload area for details.');
      }
    },
    openRemoveImageModal(image: KeywordV2['images'][number]) {
      this.removeImageModal.image = image;
    },
    closeRemoveImageModal() {
      this.removeImageModal.image = null;
      this.removeImageModal.deleteFromAllKeywords = false;
    },
    async removeImage() {
      if (this.editKeywordModal.keyword && this.removeImageModal.image) {
        try {
          if (this.removeImageModal.deleteFromAllKeywords) {
            const queryParams = new URLSearchParams();
            queryParams.append('hash', this.removeImageModal.image.hash);
            await api.execute('delete', `/images-archive-v2?${queryParams.toString()}`);
            for (const keyword of this.keywords) {
              keyword.images = keyword.images.filter(el => el.hash !== this.removeImageModal.image?.hash);
            }
            this.removeImageModal.deleteFromAllKeywords = false;
          } else {
            await api.execute('delete', `/images-archive-v2/${this.removeImageModal.image.id}`);
            this.editKeywordModal.keyword.images = this.editKeywordModal.keyword.images.filter(el => el.id !== this.removeImageModal.image?.id);
          }
          this.closeRemoveImageModal();
          toasted.showSuccessMessage('Image deleted');
        } catch (e) {
          console.error(e);
          toasted.showErrorMessage(getErrorMessage(e));
        }
      }
    },
    // copying images
    setFromKeyword(keyword: KeywordV2) {
      if (keyword && this.fromKeyword !== keyword) {
        this.fromKeyword = keyword;
      } else {
        this.resetFromKeywordToKeyword();
      }
    },
    resetFromKeywordToKeyword() {
      this.fromKeyword = null;
      this.toKeywords = [];
    },
    checkToKeyword(keyword: KeywordV2) {
      if (this.toKeywords.includes(keyword)) {
        this.toKeywords = this.toKeywords.filter(el => el !== keyword);
      } else {
        this.toKeywords = this.toKeywords.concat(keyword);
      }
    },
    async copyImages() {
      const {fromKeyword, toKeywords} = this;
      if (fromKeyword && fromKeyword.images.length && toKeywords.length) {
        const promises: Promise<void>[] = [];
        for (const keyword of toKeywords) {
          for (const image of fromKeyword.images) {
            // skip existing images
            if (keyword.images.some(el => el.hash === image.hash)) continue;
            promises.push(this.createImage(keyword, image));
          }
        }
        await Promise.all(promises);
        this.resetFromKeywordToKeyword();
      }
    },
    async createImage(keyword: KeywordV2, image: KeywordV2['images'][number]) {
      const formData = new FormData();
      formData.append('keyword', keyword.keyword);
      formData.append('market', keyword.market);
      formData.append('hash', image.hash);
      formData.append('url', image.url);
      formData.append('etag', image.etag);
      const newImage = await api.execute('post', '/images-archive-v2', formData) as ImagesArchiveV2;
      keyword.images = keyword.images.concat(newImage);
    },
    async downloadFile() {
      if (this.isDownloadingFile) return;

      try {
        this.isDownloadingFile = true;
        const searchParams = new URLSearchParams();
        this.filter.buildSearchParams(searchParams);
        await api.downloadKeywordsV2(searchParams);
      } catch (e) {
        console.error(e);
        toasted.showErrorMessage(getErrorMessage(e));
      } finally {
        this.isDownloadingFile = false;
      }
    },
    showImagePreview(event: Event, image: KeywordV2['images'][number]) {
      const previewWidth = 504;
      const previewGap = 12;
      const previewChromeHeight = 86;
      const target = event.currentTarget as HTMLElement | null;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const viewportPadding = 16;
      const maxPanelHeight = Math.max(window.innerHeight - (viewportPadding * 2), 0);
      const maxImageHeight = Math.max(Math.min(512, maxPanelHeight - previewChromeHeight), 0);
      const availableAbove = Math.max(rect.top - viewportPadding - previewGap, 0);
      const availableBelow = Math.max(window.innerHeight - rect.bottom - viewportPadding - previewGap, 0);
      const placement = availableBelow > availableAbove ? 'below' : 'above';
      const availableHeight = placement === 'below' ? availableBelow : availableAbove;
      const maxHeight = Math.max(
        Math.min(maxImageHeight, availableHeight - previewChromeHeight),
        0,
      );
      const estimatedPanelHeight = Math.min(maxPanelHeight, maxHeight + previewChromeHeight);
      const anchorTop = placement === 'below'
        ? rect.bottom + previewGap
        : rect.top - previewGap - estimatedPanelHeight;
      const top = Math.min(
        Math.max(anchorTop, viewportPadding),
        window.innerHeight - viewportPadding - estimatedPanelHeight,
      );
      const panelWidth = Math.min(previewWidth, window.innerWidth - (viewportPadding * 2));
      const left = Math.min(
        Math.max(rect.left + (rect.width / 2) - (panelWidth / 2), viewportPadding),
        window.innerWidth - viewportPadding - panelWidth,
      );
      this.hoverPreview = {
        left,
        maxHeight,
        placement,
        top,
        url: image.url,
        policyCompliance: isNil(image.policy_compliance) ? 'Unknown' : image.policy_compliance,
        keywordAdherence: isNil(image.keyword_adherence) ? 'Unknown' : image.keyword_adherence === 1 ? 'Yes' : 'No',
        complianceClass: this.getImageComplianceClass(image),
      };
    },
    hideImagePreview() {
      this.hoverPreview = {
        left: 0,
        maxHeight: 0,
        placement: 'above',
        top: 0,
        url: '',
        policyCompliance: '',
        keywordAdherence: '',
        complianceClass: '',
      };
    },
    updateQueryParams(query: {[key: string]: any}) {
      this.$router.push({query}).catch(noop);
    },
  },
});
</script>
